"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FocusTrap } from "focus-trap-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useClickAway, useDebounce, useKeyPressEvent } from "react-use";
import { useAppWallets } from "@/components/app-wallets/AppWalletsContext";
import BellIcon from "@/components/common/icons/BellIcon";
import ChatBubbleIcon from "@/components/common/icons/ChatBubbleIcon";
import DropForgeCraftIcon from "@/components/common/icons/DropForgeCraftIcon";
import DropForgeIcon from "@/components/common/icons/DropForgeIcon";
import DropForgeLaunchIcon from "@/components/common/icons/DropForgeLaunchIcon";
import DiscoverIcon from "@/components/common/icons/DiscoverIcon";
import HomeIcon from "@/components/common/icons/HomeIcon";
import WavesIcon from "@/components/common/icons/WavesIcon";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import { DROP_FORGE_PATH, DROP_FORGE_SECTIONS, DROP_FORGE_TITLE } from "@/components/drop-forge/drop-forge.constants";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { USER_PAGE_TAB_IDS } from "@/components/user/layout/userTabs.config";
import Drop, { DropLocation } from "@/components/waves/drops/Drop";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { CommunityMemberMinimal } from "@/entities/IProfile";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getProfileTargetRoute } from "@/helpers/Helpers";
import { getActiveWaveIdFromUrl, getWaveHomeRoute, getWaveRoute } from "@/helpers/navigation.helpers";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import useCapacitor from "@/hooks/useCapacitor";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useDropForgePermissions } from "@/hooks/useDropForgePermissions";
import useLocalPreference from "@/hooks/useLocalPreference";
import { mapSidebarSectionsToPages, type SidebarPageEntry, useSidebarSections } from "@/hooks/useSidebarSections";
import { useWaveDropsSearch } from "@/hooks/useWaveDropsSearch";
import { useWaves } from "@/hooks/useWaves";
import { commonApiFetch } from "@/services/api/common-api";
import HeaderSearchModalItem, { getNftCollectionMap } from "./HeaderSearchModalItem";
import { HeaderSearchTabToggle } from "./HeaderSearchTabToggle";
import type { HeaderSearchModalItemType, NFTSearchResult, PageSearchResult } from "./HeaderSearchModalItem";
enum STATE {
  INITIAL = "INITIAL",
  LOADING = "LOADING",
  ERROR = "ERROR",
  NO_RESULTS = "NO_RESULTS",
  SUCCESS = "SUCCESS",
}
enum SEARCH_MODE {
  WAVE = "WAVE",
  SITE = "SITE",
}
enum CATEGORY {
  ALL = "ALL",
  PROFILES = "PROFILES",
  NFTS = "NFTS",
  WAVES = "WAVES",
  PAGES = "PAGES",
}
type FilterableCategory = Exclude<CATEGORY, CATEGORY.ALL>;
const FILTERABLE_CATEGORIES: FilterableCategory[] = [CATEGORY.PAGES, CATEGORY.NFTS, CATEGORY.PROFILES, CATEGORY.WAVES];
const isFilterableCategory = (category: CATEGORY): category is FilterableCategory => category !== CATEGORY.ALL;
const CATEGORY_LABELS: Record<FilterableCategory, string> = {
  [CATEGORY.PAGES]: "Pages",
  [CATEGORY.PROFILES]: "Profiles",
  [CATEGORY.NFTS]: "NFTs",
  [CATEGORY.WAVES]: "Waves"
};
const CATEGORY_PREVIEW_LIMIT = 3;
const PRIMARY_NAVIGATION_PAGES: SidebarPageEntry[] = [{
  name: "Home",
  href: "/",
  section: "Main",
  icon: HomeIcon
}, {
  name: "Waves",
  href: "/waves",
  section: "Main",
  icon: WavesIcon
}, {
  name: "Messages",
  href: "/messages",
  section: "Main",
  icon: ChatBubbleIcon
}, {
  name: "Discovery",
  href: "/discover",
  section: "Main",
  icon: DiscoverIcon
}, {
  name: "Notifications",
  href: "/notifications",
  section: "Main",
  icon: BellIcon
}];
const MIN_SEARCH_LENGTH = 3;
const NFT_SEARCH_MIN_LENGTH = 3;
const HEADER_SEARCH_RESULTS_PANEL_ID = "header-search-results-panel";
interface PreviewGroupItem {
  readonly item: HeaderSearchModalItemType;
  readonly index: number;
}
interface PreviewGroup {
  readonly category: FilterableCategory;
  readonly items: PreviewGroupItem[];
  readonly total: number;
}
interface RankedPageMatch {
  readonly page: PageSearchResult;
  readonly normalizedTitle: string;
  readonly priority: number;
}
const isPageResult = (item: HeaderSearchModalItemType): item is PageSearchResult => (item as {
  readonly type?: unknown;
}).type === "PAGE";
const isNftResult = (item: HeaderSearchModalItemType): item is NFTSearchResult => Object.hasOwn(item, "contract");
const isProfileResult = (item: HeaderSearchModalItemType): item is CommunityMemberMinimal => Object.hasOwn(item, "wallet");
const isWaveResult = (item: HeaderSearchModalItemType): item is ApiWave => Object.hasOwn(item, "serial_no");
const getHeaderSearchItemKey = (item: HeaderSearchModalItemType): string => {
  if (isPageResult(item)) {
    return `page:${item.href}`;
  }
  if (isNftResult(item)) {
    return `nft:${item.contract}:${item.id}`;
  }
  if (isProfileResult(item)) {
    const base = (item.profile_id ?? item.wallet).toLowerCase();
    return `profile:${base}`;
  }
  if (isWaveResult(item)) {
    return `wave:${item.id}`;
  }
  return JSON.stringify(item);
};
interface HeaderSearchSiteResultsProps {
  readonly selectedCategory: CATEGORY;
  readonly setSelectedCategory: (category: CATEGORY) => void;
  readonly resultsByCategory: Record<FilterableCategory, HeaderSearchModalItemType[]>;
  readonly categoriesWithResults: readonly FilterableCategory[];
  readonly derivedState: STATE;
  readonly debouncedValue: string;
  readonly onClose: () => void;
  readonly onRetry: () => void;
  readonly isFetchingProfiles: boolean;
  readonly isFetchingNfts: boolean;
  readonly isFetchingWaves: boolean;
  readonly shouldShowCountdown: boolean;
  readonly charactersRemaining: number;
  readonly resultsPanelRef: React.RefObject<HTMLDivElement | null>;
}
function HeaderSearchSiteResults({
  selectedCategory,
  setSelectedCategory,
  resultsByCategory,
  categoriesWithResults,
  derivedState,
  debouncedValue,
  onClose,
  onRetry,
  isFetchingProfiles,
  isFetchingNfts,
  isFetchingWaves,
  shouldShowCountdown,
  charactersRemaining,
  resultsPanelRef
}: HeaderSearchSiteResultsProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    isApp
  } = useDeviceInfo();
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(0);
  const activeElementRef = useRef<HTMLDivElement>(null);
  const tabOptions = useMemo(() => [CATEGORY.ALL, ...categoriesWithResults].map(category => ({
    key: category,
    label: category === CATEGORY.ALL ? "All" : CATEGORY_LABELS[category as FilterableCategory],
    panelId: HEADER_SEARCH_RESULTS_PANEL_ID
  })), [categoriesWithResults]);
  const shouldRenderCategoryToggle = categoriesWithResults.length > 0 || selectedCategory !== CATEGORY.ALL;
  const previewGroups = useMemo<PreviewGroup[]>(() => {
    if (selectedCategory !== CATEGORY.ALL) {
      return [];
    }
    let runningIndex = 0;
    return categoriesWithResults.map<PreviewGroup>(category_0 => {
      const items = resultsByCategory[category_0];
      const previewItems = items.slice(0, CATEGORY_PREVIEW_LIMIT).map(item => ({
        item,
        index: runningIndex++
      }));
      return {
        category: category_0,
        items: previewItems,
        total: items.length
      };
    });
  }, [categoriesWithResults, resultsByCategory, selectedCategory]);
  const flattenedItems = useMemo(() => {
    if (selectedCategory === CATEGORY.ALL) {
      return previewGroups.flatMap(group => group.items.map(entry => entry.item));
    }
    if (isFilterableCategory(selectedCategory)) {
      return resultsByCategory[selectedCategory];
    }
    return [];
  }, [previewGroups, resultsByCategory, selectedCategory]);
  const handleCategorySelect = (category_1: CATEGORY) => {
    setSelectedCategory(category_1);
    setSelectedItemIndex(0);
  };
  const handleTabSelect = (key: string) => {
    handleCategorySelect(key as CATEGORY);
  };
  const handleViewAll = (category_2: FilterableCategory) => {
    handleCategorySelect(category_2);
  };
  const onHover = (index: number, state: boolean) => {
    if (!state) return;
    setSelectedItemIndex(index);
  };
  const goToProfile = (profile: CommunityMemberMinimal) => {
    const handleOrWallet = profile.handle ?? profile.wallet.toLowerCase();
    const path = getProfileTargetRoute({
      handleOrWallet,
      pathname: pathname,
      defaultPath: USER_PAGE_TAB_IDS.REP
    });
    router.push(path);
    onClose();
  };
  useKeyPressEvent("ArrowDown", () => setSelectedItemIndex(index_0 => index_0 + 1 < flattenedItems.length ? index_0 + 1 : index_0));
  useKeyPressEvent("ArrowUp", () => setSelectedItemIndex(index_1 => index_1 > 0 ? index_1 - 1 : index_1));
  useKeyPressEvent("Enter", () => {
    if (derivedState !== STATE.SUCCESS) return;
    const item_0 = flattenedItems[selectedItemIndex];
    if (!item_0) return;
    if (isPageResult(item_0)) {
      router.push(item_0.href);
      onClose();
      return;
    }
    if (isNftResult(item_0)) {
      const collectionMap = getNftCollectionMap();
      const key_0 = item_0.contract.toLowerCase();
      const collection = collectionMap[key_0];
      if (!collection) return;
      router.push(`${collection.path}/${item_0.id}`);
      onClose();
      return;
    }
    if (isProfileResult(item_0)) {
      goToProfile(item_0);
      return;
    }
    if (isWaveResult(item_0)) {
      const currentWaveId = getActiveWaveIdFromUrl({
        pathname,
        searchParams
      }) ?? undefined;
      const isDirectMessage = item_0.chat.scope.group?.is_direct_message ?? false;
      const target = currentWaveId === item_0.id ? getWaveHomeRoute({
        isDirectMessage,
        isApp
      }) : getWaveRoute({
        waveId: item_0.id,
        isDirectMessage,
        isApp
      });
      router.push(target);
      onClose();
    }
  });
  useEffect(() => {
    if (activeElementRef.current) {
      activeElementRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start"
      });
    }
  }, [selectedItemIndex]);
  const renderItem = (item_1: HeaderSearchModalItemType, index_2: number) => <div ref={index_2 === selectedItemIndex ? activeElementRef : null} key={getHeaderSearchItemKey(item_1)}>
      <HeaderSearchModalItem content={item_1} searchValue={debouncedValue} isSelected={index_2 === selectedItemIndex} onHover={state_0 => onHover(index_2, state_0)} onClose={onClose} />
    </div>;
  const renderItems = (items_0: HeaderSearchModalItemType[], offset = 0) => items_0.map((item_2, index_3) => renderItem(item_2, offset + index_3));
  const renderSuccessContent = () => {
    if (selectedCategory === CATEGORY.ALL) {
      return previewGroups.map(group_0 => <section key={group_0.category} className="tw-mb-4 last:tw-mb-0">
          <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
            <h3 className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-400">
              {CATEGORY_LABELS[group_0.category]}
            </h3>
            {group_0.total > group_0.items.length && <button type="button" onClick={() => handleViewAll(group_0.category)} className="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-iron-700 tw-bg-iron-900 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">
                View all {CATEGORY_LABELS[group_0.category]}
              </button>}
          </div>
          <div className="tw-space-y-1.5">
            {group_0.items.map(({
            item: item_3,
            index: index_4
          }) => renderItem(item_3, index_4))}
          </div>
        </section>);
    }
    if (isFilterableCategory(selectedCategory)) {
      return renderItems(resultsByCategory[selectedCategory]);
    }
    return null;
  };
  const isRetryPending = selectedCategory === CATEGORY.NFTS && isFetchingNfts || selectedCategory === CATEGORY.PROFILES && isFetchingProfiles || selectedCategory === CATEGORY.WAVES && isFetchingWaves;
  const renderResultsPanel = () => {
    if (derivedState === STATE.SUCCESS) {
      return <div ref={resultsPanelRef} id={HEADER_SEARCH_RESULTS_PANEL_ID} role="tabpanel" className="tw-h-0 tw-min-h-0 tw-flex-1 tw-scroll-py-2 tw-overflow-y-auto tw-px-4 tw-pb-3 tw-pt-5 tw-text-sm tw-text-iron-200 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300 md:tw-pl-0 md:tw-pr-4">
          {renderSuccessContent()}
        </div>;
    }
    if (derivedState === STATE.LOADING) {
      return <div ref={resultsPanelRef} id={HEADER_SEARCH_RESULTS_PANEL_ID} role="tabpanel" className="tw-flex tw-h-0 tw-min-h-0 tw-flex-1 tw-items-center tw-justify-center tw-px-4 md:tw-px-0">
          <p className="tw-text-sm tw-font-normal tw-text-iron-300">
            Loading...
          </p>
        </div>;
    }
    if (derivedState === STATE.NO_RESULTS) {
      return <div ref={resultsPanelRef} id={HEADER_SEARCH_RESULTS_PANEL_ID} role="tabpanel" className="tw-flex tw-h-0 tw-min-h-0 tw-flex-1 tw-items-center tw-justify-center tw-px-4 md:tw-px-0">
          <p className="tw-text-sm tw-text-iron-300">No results found</p>
        </div>;
    }
    if (derivedState === STATE.ERROR) {
      return <div ref={resultsPanelRef} id={HEADER_SEARCH_RESULTS_PANEL_ID} role="tabpanel" className="tw-flex tw-h-0 tw-min-h-0 tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-px-4 tw-text-center md:tw-px-0">
          <p className="tw-text-sm tw-font-normal tw-text-iron-300" aria-live="polite">
            Something went wrong while searching. Please try again.
          </p>
          <button type="button" onClick={onRetry} disabled={isRetryPending} aria-busy={isRetryPending ? true : undefined} className="tw-items-center tw-rounded-full tw-border tw-border-iron-300 tw-bg-iron-100 tw-px-3 tw-py-1.5 tw-font-medium tw-text-iron-800 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-200">
            Try Again
          </button>
        </div>;
    }
    return <div ref={resultsPanelRef} id={HEADER_SEARCH_RESULTS_PANEL_ID} role="tabpanel" className="tw-flex tw-h-0 tw-min-h-0 tw-flex-1 tw-items-center tw-justify-center tw-px-4 md:tw-px-0">
        <p className="tw-text-center tw-text-sm tw-font-normal tw-text-iron-300">
          Start typing to search 6529.io
          {shouldShowCountdown && ` (${charactersRemaining} more character${charactersRemaining === 1 ? "" : "s"})`}
        </p>
      </div>;
  };
  return <>
      {shouldRenderCategoryToggle && <div className="tw-px-4 tw-py-3 md:tw-hidden">
          <HeaderSearchTabToggle options={tabOptions} activeKey={selectedCategory} onSelect={handleTabSelect} fullWidth />
        </div>}

      <div className={`tw-flex tw-min-h-0 tw-flex-1 tw-flex-col md:tw-gap-4 md:tw-px-5 md:tw-pb-5 ${shouldRenderCategoryToggle ? "md:tw-grid md:tw-grid-cols-[12rem_minmax(0,1fr)]" : "md:tw-flex-row"}`}>
        {shouldRenderCategoryToggle && <aside className="tw-hidden md:tw-flex md:tw-flex-col md:tw-gap-2 md:tw-pt-5">
            <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-2xl tw-border tw-border-iron-900/60 tw-bg-iron-950/80">
              <HeaderSearchTabToggle options={tabOptions} activeKey={selectedCategory} onSelect={handleTabSelect} fullWidth orientation="vertical" />
            </div>
          </aside>}
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col md:tw-min-w-0">
          {renderResultsPanel()}
        </div>
      </div>
    </>;
}
const PAGE_SEARCH_ALIASES_BY_HREF: Record<string, string[]> = {
  [DROP_FORGE_PATH]: [DROP_FORGE_TITLE],
  [DROP_FORGE_SECTIONS.CRAFT.path]: [`${DROP_FORGE_TITLE} Craft`],
  [DROP_FORGE_SECTIONS.LAUNCH.path]: [`${DROP_FORGE_TITLE} Launch`]
};
const singularizePageSearchToken = (token: string): string => {
  if (token.length <= 3) {
    return token;
  }
  if (token.endsWith("ies") && token.length > 4) {
    return `${token.slice(0, -3)}y`;
  }
  if (token.endsWith("ches") || token.endsWith("shes") || token.endsWith("xes") || token.endsWith("zes") || token.endsWith("sses")) {
    return token.slice(0, -2);
  }
  if (token.endsWith("s") && !token.endsWith("ss")) {
    return token.slice(0, -1);
  }
  return token;
};
const getPageSearchTokens = (value: string): string[] => value.toLowerCase().split(/[^a-z0-9]+/g).filter(Boolean).map(singularizePageSearchToken);
const pageTokensMatchInOrder = (candidateTokens: readonly string[], queryTokens: readonly string[], matcher: (candidateToken: string, queryToken: string) => boolean) => {
  if (queryTokens.length === 0) {
    return false;
  }
  let candidateIndex = 0;
  return queryTokens.every(queryToken => {
    while (candidateIndex < candidateTokens.length) {
      const candidateToken = candidateTokens[candidateIndex];
      candidateIndex += 1;
      if (candidateToken !== undefined && matcher(candidateToken, queryToken)) {
        return true;
      }
    }
    return false;
  });
};
const hasCanonicalPageTokenMatch = (values: readonly string[], queryTokens: readonly string[]) => {
  if (queryTokens.length === 0) {
    return false;
  }
  return values.some(value => {
    const candidateTokens = getPageSearchTokens(value);
    return pageTokensMatchInOrder(candidateTokens, queryTokens, (candidateToken, queryToken) => candidateToken === queryToken);
  });
};
const hasExactCanonicalPageTokenMatch = (values: readonly string[], queryTokens: readonly string[]) => {
  if (queryTokens.length === 0) {
    return false;
  }
  return values.some(value => {
    const candidateTokens = getPageSearchTokens(value);
    return candidateTokens.length === queryTokens.length && candidateTokens.every((token, index) => token === queryTokens[index]);
  });
};
const hasCanonicalPageTokenPrefixMatch = (values: readonly string[], queryTokens: readonly string[]) => {
  if (queryTokens.length === 0) {
    return false;
  }
  return values.some(value => {
    const candidateTokens = getPageSearchTokens(value);
    return pageTokensMatchInOrder(candidateTokens, queryTokens, (candidateToken, queryToken) => candidateToken.startsWith(queryToken));
  });
};
const getCompositePageSearchValues = (normalizedTitle: string, normalizedBreadcrumbs: readonly string[], normalizedSearchTerms: readonly string[]): string[] => {
  const values = normalizedBreadcrumbs.flatMap((_, index) => {
    const breadcrumbSuffix = normalizedBreadcrumbs.slice(index);
    const suffixPrefix = breadcrumbSuffix.join(" ");
    return [normalizedTitle, ...normalizedSearchTerms].map(value => [suffixPrefix, value].filter(Boolean).join(" ")).filter(Boolean);
  });
  return [...new Set(values)];
};
type PageSearchMatchInputs = {
  readonly normalizedTitle: string;
  readonly normalizedHref: string;
  readonly hrefSegments: string[];
  readonly normalizedBreadcrumbs: string[];
  readonly normalizedSearchTerms: string[];
  readonly compositeValues: string[];
};
const getPageMatchPriority = ({
  normalizedTitle,
  normalizedHref,
  hrefSegments,
  normalizedBreadcrumbs,
  normalizedSearchTerms,
  compositeValues
}: PageSearchMatchInputs, normalizedQuery: string, canonicalQueryTokens: readonly string[]): number => {
  const isPathLikeQuery = normalizedQuery.startsWith("/") || normalizedQuery.includes("/");
  const titleValues = [normalizedTitle];
  const hrefValues = [normalizedHref, ...hrefSegments];
  const checks = [normalizedTitle === normalizedQuery, normalizedHref === normalizedQuery, isPathLikeQuery && normalizedHref.startsWith(normalizedQuery), isPathLikeQuery && normalizedHref.includes(normalizedQuery), hasExactCanonicalPageTokenMatch(titleValues, canonicalQueryTokens), hrefSegments.includes(normalizedQuery), normalizedSearchTerms.includes(normalizedQuery), hasExactCanonicalPageTokenMatch(normalizedSearchTerms, canonicalQueryTokens), compositeValues.includes(normalizedQuery), hasExactCanonicalPageTokenMatch(compositeValues, canonicalQueryTokens), normalizedTitle.startsWith(normalizedQuery), hasCanonicalPageTokenMatch(titleValues, canonicalQueryTokens), hasCanonicalPageTokenPrefixMatch(titleValues, canonicalQueryTokens), normalizedBreadcrumbs.includes(normalizedQuery), hasExactCanonicalPageTokenMatch(normalizedBreadcrumbs, canonicalQueryTokens), hasCanonicalPageTokenPrefixMatch(normalizedBreadcrumbs, canonicalQueryTokens), normalizedSearchTerms.some(term => term.includes(normalizedQuery)), hasCanonicalPageTokenMatch(normalizedSearchTerms, canonicalQueryTokens), hasCanonicalPageTokenPrefixMatch(normalizedSearchTerms, canonicalQueryTokens), hasCanonicalPageTokenMatch(compositeValues, canonicalQueryTokens), hasCanonicalPageTokenPrefixMatch(compositeValues, canonicalQueryTokens), normalizedTitle.includes(normalizedQuery), hrefSegments.some(segment => segment.includes(normalizedQuery)), hasCanonicalPageTokenMatch(hrefValues, canonicalQueryTokens), hasCanonicalPageTokenPrefixMatch(hrefValues, canonicalQueryTokens), hasCanonicalPageTokenMatch(normalizedBreadcrumbs, canonicalQueryTokens)];
  const matchIndex = checks.findIndex(Boolean);
  return matchIndex === -1 ? checks.length : matchIndex;
};
const pageMatchesQuery = (normalizedTitle: string, normalizedHref: string, normalizedBreadcrumbs: string[], normalizedSearchTerms: string[], compositeValues: string[], normalizedQuery: string, canonicalQueryTokens: readonly string[]) => {
  const isPathLikeQuery = normalizedQuery.startsWith("/") || normalizedQuery.includes("/");
  if (normalizedTitle.includes(normalizedQuery)) return true;
  if (isPathLikeQuery && normalizedHref.startsWith(normalizedQuery)) {
    return true;
  }
  if (isPathLikeQuery && normalizedHref.includes(normalizedQuery)) {
    return true;
  }
  if (normalizedSearchTerms.some(term => term.includes(normalizedQuery))) {
    return true;
  }
  if (normalizedBreadcrumbs.some(breadcrumb => breadcrumb.includes(normalizedQuery))) {
    return true;
  }
  if (compositeValues.some(value => value.includes(normalizedQuery))) {
    return true;
  }
  return hasCanonicalPageTokenMatch([normalizedTitle, normalizedHref, ...normalizedBreadcrumbs, ...normalizedSearchTerms, ...compositeValues], canonicalQueryTokens) || hasCanonicalPageTokenPrefixMatch([normalizedTitle, normalizedHref, ...normalizedBreadcrumbs, ...normalizedSearchTerms, ...compositeValues], canonicalQueryTokens);
};
export default function HeaderSearchModal(t0) {
  const $ = _c(163);
  const {
    onClose,
    wave
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const modalRef = useRef(null);
  useClickAway(modalRef, onClose);
  useKeyPressEvent("Escape", onClose);
  const waveChatScroll = useWaveChatScrollOptional();
  const [searchMode, setSearchMode] = useState(wave ? SEARCH_MODE.WAVE : SEARCH_MODE.SITE);
  const [searchValue, setSearchValue] = useState("");
  const [selectedCategory, setSelectedCategory] = useLocalPreference("headerSearchCategoryFilter", CATEGORY.ALL, _temp);
  const [debouncedValue, setDebouncedValue] = useState("");
  let t1;
  if ($[0] !== searchValue) {
    t1 = () => {
      setDebouncedValue(searchValue);
    };
    $[0] = searchValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== searchValue) {
    t2 = [searchValue];
    $[2] = searchValue;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useDebounce(t1, 500, t2);
  let t3;
  if ($[4] !== searchValue) {
    t3 = searchValue.trim();
    $[4] = searchValue;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const trimmedSearchValue = t3;
  let t4;
  if ($[6] !== debouncedValue) {
    t4 = debouncedValue.trim();
    $[6] = debouncedValue;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const trimmedDebouncedValue = t4;
  const searchInputLength = trimmedSearchValue.length;
  const meetsCharacterThreshold = searchInputLength >= MIN_SEARCH_LENGTH;
  const shouldSearchPages = meetsCharacterThreshold;
  const shouldSearchDefault = trimmedDebouncedValue.length >= MIN_SEARCH_LENGTH;
  let t5;
  if ($[8] !== trimmedDebouncedValue) {
    t5 = trimmedDebouncedValue.length >= NFT_SEARCH_MIN_LENGTH || trimmedDebouncedValue.length > 0 && !Number.isNaN(Number(trimmedDebouncedValue));
    $[8] = trimmedDebouncedValue;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const shouldSearchNfts = t5;
  const hasActiveDebouncedSearch = shouldSearchNfts;
  const [waveSearchDebouncedValue, setWaveSearchDebouncedValue] = useState("");
  let t6;
  if ($[10] !== searchValue) {
    t6 = () => {
      setWaveSearchDebouncedValue(searchValue);
    };
    $[10] = searchValue;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== searchValue) {
    t7 = [searchValue];
    $[12] = searchValue;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  useDebounce(t6, 250, t7);
  let t8;
  if ($[14] !== waveSearchDebouncedValue) {
    t8 = waveSearchDebouncedValue.trim();
    $[14] = waveSearchDebouncedValue;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const trimmedWaveSearchValue = t8;
  const shouldSearchWave = wave !== null && searchMode === SEARCH_MODE.WAVE && trimmedWaveSearchValue.length >= 2;
  let t9;
  if ($[16] !== wave) {
    t9 = {
      wave
    };
    $[16] = wave;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const {
    winningThreshold,
    isVotingClosed,
    isVotingControlsLocked
  } = useApprovalWaveStatus(t9);
  let t10;
  if ($[18] !== shouldSearchWave || $[19] !== trimmedWaveSearchValue || $[20] !== wave) {
    t10 = {
      wave,
      term: trimmedWaveSearchValue,
      enabled: shouldSearchWave,
      size: 50
    };
    $[18] = shouldSearchWave;
    $[19] = trimmedWaveSearchValue;
    $[20] = wave;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const {
    drops: waveDropResults,
    isLoading: isLoadingWaveDrops,
    isError: isWaveDropsError,
    hasNextPage: waveDropsHasNextPage,
    fetchNextPage: fetchNextWaveDropsPage,
    isFetchingNextPage: isFetchingNextWaveDropsPage
  } = useWaveDropsSearch(t10);
  const {
    appWalletsSupported
  } = useAppWallets();
  const {
    country
  } = useCookieConsent();
  const capacitor = useCapacitor();
  const sections = useSidebarSections(appWalletsSupported, capacitor.isIos, country);
  let t11;
  if ($[22] !== sections) {
    t11 = mapSidebarSectionsToPages(sections);
    $[22] = sections;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const sidebarPages = t11;
  const {
    canAccessLanding,
    canAccessCraft,
    canAccessLaunch
  } = useDropForgePermissions();
  let t12;
  bb0: {
    if (!canAccessLanding) {
      let t13;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = [];
        $[24] = t13;
      } else {
        t13 = $[24];
      }
      t12 = t13;
      break bb0;
    }
    let t13;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = {
        name: DROP_FORGE_TITLE,
        href: DROP_FORGE_PATH,
        section: "Main",
        icon: DropForgeIcon
      };
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let pages;
    if ($[26] !== canAccessCraft || $[27] !== canAccessLaunch) {
      pages = [t13];
      if (canAccessCraft) {
        let t14;
        if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
          t14 = {
            name: DROP_FORGE_SECTIONS.CRAFT.title,
            href: DROP_FORGE_SECTIONS.CRAFT.path,
            section: DROP_FORGE_TITLE,
            icon: DropForgeCraftIcon
          };
          $[29] = t14;
        } else {
          t14 = $[29];
        }
        pages.push(t14);
      }
      if (canAccessLaunch) {
        let t14;
        if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
          t14 = {
            name: DROP_FORGE_SECTIONS.LAUNCH.title,
            href: DROP_FORGE_SECTIONS.LAUNCH.path,
            section: DROP_FORGE_TITLE,
            icon: DropForgeLaunchIcon
          };
          $[30] = t14;
        } else {
          t14 = $[30];
        }
        pages.push(t14);
      }
      $[26] = canAccessCraft;
      $[27] = canAccessLaunch;
      $[28] = pages;
    } else {
      pages = $[28];
    }
    t12 = pages;
  }
  const dropForgePages = t12;
  let t13;
  if ($[31] !== dropForgePages || $[32] !== sidebarPages) {
    const seen = new Set();
    t13 = [...PRIMARY_NAVIGATION_PAGES, ...sidebarPages, ...dropForgePages].filter(entry => {
      if (seen.has(entry.href)) {
        return false;
      }
      seen.add(entry.href);
      return true;
    });
    $[31] = dropForgePages;
    $[32] = sidebarPages;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const allPageEntries = t13;
  let t14;
  if ($[34] !== allPageEntries) {
    t14 = allPageEntries.map(_temp4);
    $[34] = allPageEntries;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const pageCatalog = t14;
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = event => {
      const newValue = event.target.value;
      setSearchValue(newValue);
    };
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const handleInputChange = t15;
  const inputRef = useRef(null);
  let t16;
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };
    t17 = [];
    $[37] = t16;
    $[38] = t17;
  } else {
    t16 = $[37];
    t17 = $[38];
  }
  useEffect(t16, t17);
  const resultsPanelRef = useRef(null);
  let t18;
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      if (typeof document === "undefined") {
        return;
      }
      const previousOverflow = document.body.style.overflow;
      const previousPaddingRight = document.body.style.paddingRight;
      const scrollbarGap = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      if (scrollbarGap > 0) {
        document.body.style.paddingRight = `${scrollbarGap}px`;
      }
      const handleWheel = event_0 => {
        event_0.preventDefault();
        if (resultsPanelRef.current) {
          resultsPanelRef.current.scrollBy({
            top: event_0.deltaY,
            left: event_0.deltaX
          });
        }
      };
      window.addEventListener("wheel", handleWheel, {
        passive: false
      });
      return () => {
        document.body.style.overflow = previousOverflow;
        document.body.style.paddingRight = previousPaddingRight;
        window.removeEventListener("wheel", handleWheel);
      };
    };
    t19 = [];
    $[39] = t18;
    $[40] = t19;
  } else {
    t18 = $[39];
    t19 = $[40];
  }
  useEffect(t18, t19);
  let t20;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      placeholderData: keepPreviousData
    };
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const sharedQueryDefaults = t20 as const;
  let t21;
  let t22;
  if ($[42] !== trimmedDebouncedValue) {
    t21 = [QueryKey.PROFILE_SEARCH, trimmedDebouncedValue];
    t22 = async () => await commonApiFetch({
      endpoint: "community-members",
      params: {
        param: trimmedDebouncedValue
      }
    });
    $[42] = trimmedDebouncedValue;
    $[43] = t21;
    $[44] = t22;
  } else {
    t21 = $[43];
    t22 = $[44];
  }
  let t23;
  if ($[45] !== shouldSearchDefault || $[46] !== t21 || $[47] !== t22) {
    t23 = {
      queryKey: t21,
      queryFn: t22,
      enabled: shouldSearchDefault,
      ...sharedQueryDefaults
    };
    $[45] = shouldSearchDefault;
    $[46] = t21;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  const {
    isFetching: isFetchingProfiles,
    data: profiles,
    error: profilesError,
    refetch: refetchProfiles
  } = useQuery(t23);
  let t24;
  let t25;
  if ($[49] !== trimmedDebouncedValue) {
    t24 = [QueryKey.NFTS_SEARCH, trimmedDebouncedValue];
    t25 = async () => await commonApiFetch({
      endpoint: "nfts_search",
      params: {
        search: trimmedDebouncedValue
      }
    });
    $[49] = trimmedDebouncedValue;
    $[50] = t24;
    $[51] = t25;
  } else {
    t24 = $[50];
    t25 = $[51];
  }
  let t26;
  if ($[52] !== shouldSearchNfts || $[53] !== t24 || $[54] !== t25) {
    t26 = {
      queryKey: t24,
      queryFn: t25,
      enabled: shouldSearchNfts,
      ...sharedQueryDefaults
    };
    $[52] = shouldSearchNfts;
    $[53] = t24;
    $[54] = t25;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  const {
    isFetching: isFetchingNfts,
    data: nfts,
    error: nftsError,
    refetch: refetchNfts
  } = useQuery(t26);
  const t27 = shouldSearchDefault ? trimmedDebouncedValue : null;
  let t28;
  if ($[56] !== shouldSearchDefault || $[57] !== t27) {
    t28 = {
      identity: null,
      waveName: t27,
      limit: 20,
      enabled: shouldSearchDefault
    };
    $[56] = shouldSearchDefault;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  const {
    waves,
    isFetching: isFetchingWaves,
    error: wavesError,
    refetch: refetchWaves
  } = useWaves(t28);
  let t29;
  if ($[59] !== pageCatalog || $[60] !== shouldSearchPages || $[61] !== trimmedSearchValue) {
    bb1: {
      if (!shouldSearchPages) {
        let t30;
        if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
          t30 = [];
          $[63] = t30;
        } else {
          t30 = $[63];
        }
        t29 = t30;
        break bb1;
      }
      const normalizedQuery = trimmedSearchValue.toLowerCase();
      const canonicalQueryTokens = getPageSearchTokens(trimmedSearchValue);
      if (!normalizedQuery) {
        let t30;
        if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
          t30 = [];
          $[64] = t30;
        } else {
          t30 = $[64];
        }
        t29 = t30;
        break bb1;
      }
      const rankedMatches = pageCatalog.reduce((accumulator, page) => {
        const normalizedTitle = page.title.toLowerCase();
        const normalizedHref = page.href.toLowerCase();
        const normalizedBreadcrumbs = page.breadcrumbs.map(_temp5);
        const normalizedSearchTerms = (page.searchTerms ?? []).map(_temp6);
        const compositeValues = getCompositePageSearchValues(normalizedTitle, normalizedBreadcrumbs, normalizedSearchTerms);
        if (!pageMatchesQuery(normalizedTitle, normalizedHref, normalizedBreadcrumbs, normalizedSearchTerms, compositeValues, normalizedQuery, canonicalQueryTokens)) {
          return accumulator;
        }
        const hrefSegments = normalizedHref.split("/").filter(Boolean);
        const matchInputs = {
          normalizedTitle,
          normalizedHref,
          hrefSegments,
          normalizedBreadcrumbs,
          normalizedSearchTerms,
          compositeValues
        };
        accumulator.push({
          page,
          normalizedTitle,
          priority: getPageMatchPriority(matchInputs, normalizedQuery, canonicalQueryTokens)
        });
        return accumulator;
      }, []);
      t29 = rankedMatches.sort(_temp7).map(_temp8);
    }
    $[59] = pageCatalog;
    $[60] = shouldSearchPages;
    $[61] = trimmedSearchValue;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  const pageResults = t29;
  let t30;
  if ($[65] !== profiles || $[66] !== shouldSearchDefault) {
    t30 = shouldSearchDefault ? profiles ?? [] : [];
    $[65] = profiles;
    $[66] = shouldSearchDefault;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  const profileResults = t30;
  let t31;
  if ($[68] !== nfts || $[69] !== shouldSearchNfts) {
    t31 = shouldSearchNfts ? nfts ?? [] : [];
    $[68] = nfts;
    $[69] = shouldSearchNfts;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  const nftResults = t31;
  let t32;
  if ($[71] !== shouldSearchDefault || $[72] !== waves) {
    t32 = shouldSearchDefault ? waves ?? [] : [];
    $[71] = shouldSearchDefault;
    $[72] = waves;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  const waveResults = t32;
  const charactersRemaining = Math.max(MIN_SEARCH_LENGTH - searchInputLength, 0);
  const shouldShowCountdown = searchInputLength > 0 && charactersRemaining > 0;
  const isAwaitingDebouncedSearch = meetsCharacterThreshold && trimmedDebouncedValue !== trimmedSearchValue;
  const isSearching = shouldSearchPages || hasActiveDebouncedSearch;
  let t33;
  if ($[74] !== nftResults || $[75] !== pageResults || $[76] !== profileResults || $[77] !== waveResults) {
    t33 = {
      [CATEGORY.PAGES]: pageResults,
      [CATEGORY.PROFILES]: profileResults,
      [CATEGORY.NFTS]: nftResults,
      [CATEGORY.WAVES]: waveResults
    };
    $[74] = nftResults;
    $[75] = pageResults;
    $[76] = profileResults;
    $[77] = waveResults;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  const resultsByCategory = t33;
  let t34;
  if ($[79] !== resultsByCategory) {
    t34 = FILTERABLE_CATEGORIES.filter(category => resultsByCategory[category].length > 0);
    $[79] = resultsByCategory;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  const categoriesWithResults = t34;
  let t35;
  let t36;
  if ($[81] !== categoriesWithResults || $[82] !== selectedCategory || $[83] !== setSelectedCategory) {
    t35 = () => {
      if (selectedCategory !== CATEGORY.ALL && (!isFilterableCategory(selectedCategory) || !categoriesWithResults.includes(selectedCategory))) {
        setSelectedCategory(CATEGORY.ALL);
      }
    };
    t36 = [categoriesWithResults, selectedCategory, setSelectedCategory];
    $[81] = categoriesWithResults;
    $[82] = selectedCategory;
    $[83] = setSelectedCategory;
    $[84] = t35;
    $[85] = t36;
  } else {
    t35 = $[84];
    t36 = $[85];
  }
  useEffect(t35, t36);
  let t37;
  if ($[86] !== setSelectedCategory) {
    t37 = () => {
      setSearchValue("");
      setDebouncedValue("");
      setWaveSearchDebouncedValue("");
      setSelectedCategory(CATEGORY.ALL);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    };
    $[86] = setSelectedCategory;
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  const handleClearSearch = t37;
  let t38;
  if ($[88] !== onClose || $[89] !== pathname || $[90] !== router || $[91] !== searchParams || $[92] !== wave || $[93] !== waveChatScroll) {
    t38 = serialNo => {
      if (!wave) {
        return;
      }
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
      } else {
        const params = new URLSearchParams(searchParams?.toString() || "");
        params.set("serialNo", String(serialNo));
        router.replace(`${pathname}?${params.toString()}`, {
          scroll: false
        });
      }
      onClose();
    };
    $[88] = onClose;
    $[89] = pathname;
    $[90] = router;
    $[91] = searchParams;
    $[92] = wave;
    $[93] = waveChatScroll;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  const handleWaveDropSelect = t38;
  let t39;
  bb2: {
    if (!isSearching) {
      t39 = STATE.INITIAL;
      break bb2;
    }
    const hasResults = categoriesWithResults.length > 0;
    if (hasResults) {
      t39 = STATE.SUCCESS;
      break bb2;
    }
    if (isAwaitingDebouncedSearch) {
      t39 = STATE.LOADING;
      break bb2;
    }
    const anyFetching = shouldSearchDefault && (isFetchingProfiles || isFetchingWaves) || shouldSearchNfts && isFetchingNfts;
    if (anyFetching) {
      t39 = STATE.LOADING;
      break bb2;
    }
    const anyError = shouldSearchDefault && (Boolean(profilesError) || Boolean(wavesError)) || shouldSearchNfts && Boolean(nftsError);
    if (anyError) {
      t39 = STATE.ERROR;
      break bb2;
    }
    t39 = STATE.NO_RESULTS;
  }
  const derivedState = t39;
  let t40;
  if ($[95] !== refetchNfts || $[96] !== refetchProfiles || $[97] !== refetchWaves || $[98] !== selectedCategory || $[99] !== shouldSearchDefault || $[100] !== shouldSearchNfts) {
    t40 = () => {
      if (selectedCategory === CATEGORY.PAGES) {
        return;
      }
      if (selectedCategory === CATEGORY.ALL) {
        if (shouldSearchDefault) {
          refetchProfiles();
          refetchWaves();
        }
        if (shouldSearchNfts) {
          refetchNfts();
        }
      } else {
        if (selectedCategory === CATEGORY.PROFILES) {
          if (shouldSearchDefault) {
            refetchProfiles();
          }
        } else {
          if (selectedCategory === CATEGORY.NFTS) {
            if (shouldSearchNfts) {
              refetchNfts();
            }
          } else {
            if (selectedCategory === CATEGORY.WAVES) {
              if (shouldSearchDefault) {
                refetchWaves();
              }
            }
          }
        }
      }
    };
    $[95] = refetchNfts;
    $[96] = refetchProfiles;
    $[97] = refetchWaves;
    $[98] = selectedCategory;
    $[99] = shouldSearchDefault;
    $[100] = shouldSearchNfts;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  const handleRetry = t40;
  let t41;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      allowOutsideClick: true,
      fallbackFocus: () => modalRef.current as HTMLElement | null ?? inputRef.current as HTMLElement | null ?? document.body,
      initialFocus: () => inputRef.current as HTMLElement | null ?? modalRef.current as HTMLElement | null ?? document.body
    };
    $[102] = t41;
  } else {
    t41 = $[102];
  }
  let t42;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <div className="tw-fixed tw-inset-0 tw-bg-gray-600 tw-bg-opacity-50 tw-backdrop-blur-[1px]" />;
    $[103] = t42;
  } else {
    t42 = $[103];
  }
  let t43;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <ChevronLeftIcon className="tw-size-6 tw-flex-shrink-0" />;
    $[104] = t43;
  } else {
    t43 = $[104];
  }
  let t44;
  if ($[105] !== onClose) {
    t44 = <button type="button" onClick={onClose} aria-label="Go back" className="-tw-ml-1 tw-mr-1 tw-flex tw-size-6 tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent tw-text-iron-300 tw-transition tw-duration-150 hover:tw-text-white sm:tw-hidden">{t43}</button>;
    $[105] = onClose;
    $[106] = t44;
  } else {
    t44 = $[106];
  }
  let t45;
  let t46;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <svg className="tw-pointer-events-none tw-absolute tw-left-4 tw-top-3.5 tw-h-5 tw-w-5 tw-text-iron-300" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>;
    t46 = <label className="tw-sr-only" htmlFor="header-search-input">Search</label>;
    $[107] = t45;
    $[108] = t46;
  } else {
    t45 = $[107];
    t46 = $[108];
  }
  const t47 = searchMode === SEARCH_MODE.WAVE ? "Search messages" : "Search 6529.io";
  let t48;
  if ($[109] !== searchValue || $[110] !== t47) {
    t48 = <input id="header-search-input" ref={inputRef} type="text" required={true} autoComplete="off" value={searchValue} onChange={handleInputChange} className="sm:text-sm tw-form-input tw-block tw-w-full tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-3 tw-pl-11 tw-pr-16 tw-text-base tw-font-normal tw-text-iron-50 tw-caret-primary-300 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out placeholder:tw-text-iron-500 hover:tw-ring-iron-600 focus:tw-bg-transparent focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-300" placeholder={t47} />;
    $[109] = searchValue;
    $[110] = t47;
    $[111] = t48;
  } else {
    t48 = $[111];
  }
  let t49;
  if ($[112] !== handleClearSearch || $[113] !== searchValue.length) {
    t49 = searchValue.length > 0 && <button type="button" onClick={handleClearSearch} aria-label="Clear search" className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-rounded-full tw-bg-transparent tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-iron-300 hover:tw-text-white">Clear</button>;
    $[112] = handleClearSearch;
    $[113] = searchValue.length;
    $[114] = t49;
  } else {
    t49 = $[114];
  }
  let t50;
  if ($[115] !== t48 || $[116] !== t49) {
    t50 = <div className="tw-relative tw-flex-1">{t45}{t46}{t48}{t49}</div>;
    $[115] = t48;
    $[116] = t49;
    $[117] = t50;
  } else {
    t50 = $[117];
  }
  let t51;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <XMarkIcon className="tw-size-5" />;
    $[118] = t51;
  } else {
    t51 = $[118];
  }
  let t52;
  if ($[119] !== onClose) {
    t52 = <button type="button" onClick={onClose} aria-label="Close search" className="tw-hidden tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-300 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white sm:tw-inline-flex">{t51}</button>;
    $[119] = onClose;
    $[120] = t52;
  } else {
    t52 = $[120];
  }
  let t53;
  if ($[121] !== t44 || $[122] !== t50 || $[123] !== t52) {
    t53 = <div className="tw-mt-4 tw-flex tw-items-center tw-gap-2 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/10 tw-px-4 tw-pb-4">{t44}{t50}{t52}</div>;
    $[121] = t44;
    $[122] = t50;
    $[123] = t52;
    $[124] = t53;
  } else {
    t53 = $[124];
  }
  let t54;
  if ($[125] !== searchMode || $[126] !== wave) {
    t54 = wave && <div className="tw-px-4 tw-pb-3 tw-pt-1"><div className="tw-inline-flex tw-gap-0.5 tw-rounded-lg tw-bg-iron-900 tw-p-0.5"><button type="button" onClick={() => setSearchMode(SEARCH_MODE.WAVE)} className={`tw-rounded-md tw-border-0 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-transition tw-duration-150 ${searchMode === SEARCH_MODE.WAVE ? "tw-bg-primary-500 tw-text-white" : "tw-bg-transparent tw-text-iron-400 hover:tw-text-iron-200"}`}>In this Wave</button><button type="button" onClick={() => setSearchMode(SEARCH_MODE.SITE)} className={`tw-rounded-md tw-border-0 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-transition tw-duration-150 ${searchMode === SEARCH_MODE.SITE ? "tw-bg-primary-500 tw-text-white" : "tw-bg-transparent tw-text-iron-400 hover:tw-text-iron-200"}`}>Site-wide</button></div></div>;
    $[125] = searchMode;
    $[126] = wave;
    $[127] = t54;
  } else {
    t54 = $[127];
  }
  let t55;
  if ($[128] !== fetchNextWaveDropsPage || $[129] !== handleWaveDropSelect || $[130] !== isFetchingNextWaveDropsPage || $[131] !== isLoadingWaveDrops || $[132] !== isVotingClosed || $[133] !== isVotingControlsLocked || $[134] !== isWaveDropsError || $[135] !== searchMode || $[136] !== shouldSearchWave || $[137] !== wave || $[138] !== waveDropResults || $[139] !== waveDropsHasNextPage || $[140] !== winningThreshold) {
    t55 = searchMode === SEARCH_MODE.WAVE && wave && <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col md:tw-flex-row md:tw-gap-4 md:tw-px-5 md:tw-pb-5"><div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col md:tw-min-w-0"><div ref={resultsPanelRef} className="tw-h-0 tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pb-6 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300">{isLoadingWaveDrops && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-iron-300">Loading…</div>}{!isLoadingWaveDrops && isWaveDropsError && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-iron-300">Couldn't load search results.</div>}{!isLoadingWaveDrops && !isWaveDropsError && !shouldSearchWave && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-sm tw-text-iron-400">Type at least 2 characters to search in {wave.name}.</div>}{!isLoadingWaveDrops && !isWaveDropsError && shouldSearchWave && waveDropResults.length === 0 && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-sm tw-text-iron-400">No matches found.</div>}{!isLoadingWaveDrops && !isWaveDropsError && shouldSearchWave && waveDropResults.length > 0 && <div className="tw-space-y-2"><div className="tw-text-xs tw-text-iron-400">{waveDropResults.length} result{waveDropResults.length === 1 ? "" : "s"}</div><div className="tw-space-y-2">{waveDropResults.map((drop, index) => {
                const previousDrop = waveDropResults[index - 1] ?? null;
                const nextDrop = waveDropResults[index + 1] ?? null;
                const serialNo_0 = drop.serial_no;
                const canSelect = typeof serialNo_0 === "number";
                return <button type="button" key={drop.stableKey} disabled={!canSelect} onClick={() => {
                  if (!canSelect) {
                    return;
                  }
                  handleWaveDropSelect(serialNo_0);
                }} className="tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950/50 tw-text-left tw-transition tw-duration-150 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:hover:tw-border-iron-600 desktop-hover:hover:tw-bg-iron-900/40"><div className="tw-pointer-events-none"><Drop drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={false} activeDrop={null} showReplyAndQuote={false} location={DropLocation.WAVE} dropViewDropId={null} onReply={_temp9} onReplyClick={_temp10} onQuoteClick={_temp11} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} /></div></button>;
              })}</div>{waveDropsHasNextPage && <div className="tw-flex tw-justify-center tw-pt-2"><button type="button" onClick={() => fetchNextWaveDropsPage()} disabled={isFetchingNextWaveDropsPage} className="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white disabled:tw-cursor-not-allowed disabled:tw-opacity-50">{isFetchingNextWaveDropsPage ? "Loading\u2026" : "Load more"}</button></div>}</div>}</div></div></div>;
    $[128] = fetchNextWaveDropsPage;
    $[129] = handleWaveDropSelect;
    $[130] = isFetchingNextWaveDropsPage;
    $[131] = isLoadingWaveDrops;
    $[132] = isVotingClosed;
    $[133] = isVotingControlsLocked;
    $[134] = isWaveDropsError;
    $[135] = searchMode;
    $[136] = shouldSearchWave;
    $[137] = wave;
    $[138] = waveDropResults;
    $[139] = waveDropsHasNextPage;
    $[140] = winningThreshold;
    $[141] = t55;
  } else {
    t55 = $[141];
  }
  let t56;
  if ($[142] !== categoriesWithResults || $[143] !== charactersRemaining || $[144] !== debouncedValue || $[145] !== derivedState || $[146] !== handleRetry || $[147] !== isFetchingNfts || $[148] !== isFetchingProfiles || $[149] !== isFetchingWaves || $[150] !== onClose || $[151] !== resultsByCategory || $[152] !== searchMode || $[153] !== selectedCategory || $[154] !== setSelectedCategory || $[155] !== shouldShowCountdown || $[156] !== trimmedDebouncedValue) {
    t56 = searchMode === SEARCH_MODE.SITE && <HeaderSearchSiteResults key={trimmedDebouncedValue} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} resultsByCategory={resultsByCategory} categoriesWithResults={categoriesWithResults} derivedState={derivedState} debouncedValue={debouncedValue} onClose={onClose} onRetry={handleRetry} isFetchingProfiles={isFetchingProfiles} isFetchingNfts={isFetchingNfts} isFetchingWaves={isFetchingWaves} shouldShowCountdown={shouldShowCountdown} charactersRemaining={charactersRemaining} resultsPanelRef={resultsPanelRef} />;
    $[142] = categoriesWithResults;
    $[143] = charactersRemaining;
    $[144] = debouncedValue;
    $[145] = derivedState;
    $[146] = handleRetry;
    $[147] = isFetchingNfts;
    $[148] = isFetchingProfiles;
    $[149] = isFetchingWaves;
    $[150] = onClose;
    $[151] = resultsByCategory;
    $[152] = searchMode;
    $[153] = selectedCategory;
    $[154] = setSelectedCategory;
    $[155] = shouldShowCountdown;
    $[156] = trimmedDebouncedValue;
    $[157] = t56;
  } else {
    t56 = $[157];
  }
  let t57;
  if ($[158] !== t53 || $[159] !== t54 || $[160] !== t55 || $[161] !== t56) {
    t57 = createPortal(<FocusTrap focusTrapOptions={t41}><div className="tailwind-scope tw-relative tw-z-1000 tw-cursor-default">{t42}<div className="tw-fixed tw-inset-0 tw-z-1000 tw-overflow-y-auto"><div className="tw-flex tw-min-h-full tw-items-start tw-justify-center tw-p-4 tw-text-center sm:tw-p-6 lg:tw-items-center"><div ref={modalRef} aria-modal="true" aria-labelledby="header-search-input" className="inset-safe-area tw-relative tw-flex tw-h-[520px] tw-max-h-[70vh] tw-min-h-0 tw-w-full tw-max-w-[min(100vw-3rem,900px)] tw-transform tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-iron-950 tw-text-left tw-shadow-xl tw-transition-all tw-duration-500 sm:tw-max-w-3xl">{t53}{t54}{t55}{t56}</div></div></div></div></FocusTrap>, document.body);
    $[158] = t53;
    $[159] = t54;
    $[160] = t55;
    $[161] = t56;
    $[162] = t57;
  } else {
    t57 = $[162];
  }
  return t57;
}
function _temp11() {}
function _temp10() {}
function _temp9() {}
function _temp8(result) {
  return result.page;
}
function _temp7(a, b) {
  if (a.priority !== b.priority) {
    return a.priority - b.priority;
  }
  const titleComparison = a.normalizedTitle.localeCompare(b.normalizedTitle);
  if (titleComparison !== 0) {
    return titleComparison;
  }
  return a.page.href.localeCompare(b.page.href);
}
function _temp6(value_3) {
  return value_3.toLowerCase();
}
function _temp5(value_2) {
  return value_2.toLowerCase();
}
function _temp4(entry_0) {
  return {
    type: "PAGE",
    title: entry_0.name,
    href: entry_0.href,
    icon: entry_0.icon,
    searchTerms: PAGE_SEARCH_ALIASES_BY_HREF[entry_0.href] ?? [],
    breadcrumbs: [entry_0.section, entry_0.subsection].filter(_temp2).map(_temp3)
  };
}
function _temp3(value_1) {
  return value_1;
}
function _temp2(value_0) {
  return !!value_0;
}
function _temp(value) {
  return Object.values(CATEGORY).includes(value);
}